import { APPOINTMENT_TYPES } from './appointmentTypes';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X, Stethoscope, ExternalLink, Trash2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { DiagnosticModal } from '../../modules/mechanic/Workshop/Diagnostics/DiagnosticModal';

export interface AppointmentLite {
    id: string;
    start_time: string;
    end_time: string;
    customer_name: string | null;
    customer_phone: string | null;
    vehicle_vrm: string | null;
    vehicle_make: string | null;
    vehicle_model: string | null;
    status: string;
    appointment_type: string | null;
    notes: string | null;
    linked_quote_id: string | null;
    linked_job_id: string | null;
}

interface Props {
    isOpen: boolean;
    appointment: AppointmentLite | null;
    onClose: () => void;
    onChanged: () => void;
}

export const AppointmentDetailsModal = ({ isOpen, appointment, onClose, onChanged }: Props) => {
    const navigate = useNavigate();
    const [type, setType] = useState<string>('checkup');
    const [notes, setNotes] = useState<string>('');
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [confirmCancel, setConfirmCancel] = useState(false);
    const [showDiagnostic, setShowDiagnostic] = useState(false);

    useEffect(() => {
        if (!appointment) return;
        setType(appointment.appointment_type || 'checkup');
        setNotes(appointment.notes || '');
        setConfirmCancel(false);
    }, [appointment]);

    if (!isOpen || !appointment) return null;

    const fmtDate = new Date(appointment.start_time).toLocaleDateString('en-GB', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
    });
    const fmtTime = new Date(appointment.start_time).toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });

    const save = async () => {
        setSaving(true);
        const { error } = await supabase
            .from('appointments')
            .update({ appointment_type: type, notes: notes || null })
            .eq('id', appointment.id);
        setSaving(false);
        if (error) {
            console.error('Update appointment failed', error);
            return;
        }
        onChanged();
        onClose();
    };

    const cancel = async () => {
        setDeleting(true);
        const { error } = await supabase
            .from('appointments')
            .update({ status: 'cancelled' })
            .eq('id', appointment.id);
        setDeleting(false);
        if (error) {
            console.error('Cancel appointment failed', error);
            return;
        }
        onChanged();
        onClose();
    };

    const openJob = () => {
        if (appointment.linked_job_id) {
            navigate(`/workshop?job_id=${appointment.linked_job_id}`);
        } else {
            navigate('/workshop');
        }
        onClose();
    };

    // Portal to body: the dashboard cards use + the Today
    // card flip uses perspective/preserve-3d. Both create new stacking
    // contexts, so a `z-[60]` rendered inside the card tree gets trapped
    // beneath sibling cards. Portalling to body escapes those contexts.
    return createPortal(
        <>
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-scrim/60"
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="fd-panel w-full max-w-md max-h-[92vh] overflow-y-auto animate-in fade-in zoom-in-95 duration-150"
            >
                <div className="sticky top-0 z-[1] [background:var(--fd-panel-face)] border-b border-line px-5 py-4 flex items-center justify-between">
                    <div>
                        <p className="text-[10px] uppercase tracking-[0.18em] font-display font-semibold text-fg-3 mb-0.5">
                            Appointment
                        </p>
                        <p className="text-base font-display font-bold text-fg">
                            {fmtDate} · {fmtTime}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-md text-fg-3 hover:text-fg hover:bg-card transition-colors"
                        aria-label="Close"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="p-5 space-y-5">
                    {/* Who + vehicle */}
                    <div className="rounded-lg bg-card border border-line p-3.5 space-y-1.5">
                        <p className="text-fg text-sm font-medium">
                            {appointment.customer_name || 'Walk-in'}
                        </p>
                        {appointment.customer_phone && (
                            <p className="text-xs font-mono text-fg-3">{appointment.customer_phone}</p>
                        )}
                        {appointment.vehicle_vrm && (
                            <div className="flex items-center gap-2 pt-1">
                                <span
                                    className="inline-block px-1.5 py-0.5 rounded-sm font-mono text-[11px] font-bold tracking-wider"
                                    style={{ backgroundColor: 'var(--fd-plate)', color: 'var(--fd-plate-ink)' }}
                                >
                                    {appointment.vehicle_vrm}
                                </span>
                                <span className="text-xs text-fg-3">
                                    {[appointment.vehicle_make, appointment.vehicle_model].filter(Boolean).join(' ')}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Type picker */}
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.18em] text-fg-3 font-display font-semibold mb-2">
                            What's it for?
                        </label>
                        <div className="grid grid-cols-3 gap-1.5">
                            {APPOINTMENT_TYPES.map((t) => {
                                const Icon = t.icon;
                                const selected = type === t.value;
                                return (
                                    <button
                                        key={t.value}
                                        onClick={() => setType(t.value)}
                                        className={`h-16 rounded-md transition-colors flex flex-col items-center justify-center gap-1 ${
                                            selected
                                                ? 'bg-gold-fill/10 border border-gold/40 text-gold'
                                                : 'bg-card border border-line text-fg-2 hover:bg-raised'
                                        }`}
                                    >
                                        <Icon className="w-4 h-4" strokeWidth={selected ? 2.25 : 1.75} />
                                        <span className="text-[11px] font-medium">{t.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Notes */}
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.18em] text-fg-3 font-display font-semibold mb-2">
                            Notes
                        </label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={3}
                            placeholder="What needs doing? Anything the mechanic should know…"
                            className="w-full px-3 py-2 bg-canvas border border-line focus:border-gold focus:shadow-focus rounded-md text-sm text-fg outline-none resize-none transition-all placeholder:text-fg-4"
                        />
                    </div>

                    {/* Start diagnostic — turns this appointment into a diagnostic
                        inspection for the same customer/vehicle. */}
                    {!appointment.linked_job_id && (
                        <button
                            onClick={() => setShowDiagnostic(true)}
                            // Copper: starting live work on the vehicle.
                            className="fd-btn fd-btn-copper w-full"
                        >
                            <Stethoscope className="w-4 h-4" />
                            Start diagnostic
                        </button>
                    )}

                    {/* Job link */}
                    {appointment.linked_job_id && (
                        <button
                            onClick={openJob}
                            className="w-full h-10 rounded-md bg-card border border-line hover:border-gold hover:text-gold text-fg-2 text-xs font-display font-semibold transition-colors inline-flex items-center justify-center gap-2"
                        >
                            <ExternalLink className="w-3.5 h-3.5" />
                            Open linked job
                        </button>
                    )}

                    {/* Actions */}
                    <div className="flex items-center justify-between pt-2">
                        {confirmCancel ? (
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={cancel}
                                    disabled={deleting}
                                    className="h-10 px-3 text-xs font-display font-semibold bg-danger-fill hover:brightness-110 text-on-fill rounded-md inline-flex items-center gap-1.5 disabled:opacity-50 transition-colors"
                                >
                                    {deleting ? 'Cancelling...' : 'Yes, cancel'}
                                </button>
                                <button
                                    onClick={() => setConfirmCancel(false)}
                                    disabled={deleting}
                                    className="h-10 px-3 text-xs font-display font-semibold text-fg-3 hover:text-fg hover:bg-card rounded-md transition-colors"
                                >
                                    Keep
                                </button>
                            </div>
                        ) : (
                            <button
                                onClick={() => setConfirmCancel(true)}
                                disabled={deleting}
                                className="h-10 px-3 text-xs font-display font-semibold text-danger hover:bg-danger/10 rounded-md inline-flex items-center gap-1.5 disabled:opacity-50 transition-colors"
                            >
                                <Trash2 className="w-3.5 h-3.5" />
                                Cancel slot
                            </button>
                        )}
                        <button
                            onClick={save}
                            disabled={saving}
                            className="h-10 px-5 fd-btn-primary font-display font-semibold text-sm rounded-md disabled:opacity-60 transition-colors"
                        >
                            {saving ? 'Saving…' : 'Save'}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <DiagnosticModal
            isOpen={showDiagnostic}
            onClose={() => setShowDiagnostic(false)}
            onSaved={() => { onChanged(); onClose(); }}
            context={{
                appointmentId: appointment.id,
                customerName: appointment.customer_name || undefined,
                vehicleVrm: appointment.vehicle_vrm || undefined,
                vehicleDesc: [appointment.vehicle_make, appointment.vehicle_model].filter(Boolean).join(' ') || undefined,
            }}
        />
        </>,
        document.body
    );
};
